import {defineStore} from 'pinia'
import {useAuthStore} from '@core/stores/authStore'

const name = 'appMenu'

// Appartenance d'une entrée de menu à l'espace candidat/recruteur :
//  1. PRIORITÉ au marqueur EXPLICITE : propriété SOS_SPACE (type Liste, valeur
//     "Recruteur" ou "Candidat") attachée au menu via son champ `options`
//     (éditable dans l'écran AppMenu). Forme stockée : {code:'SOS_SPACE',
//     type:'LIST', value:[{id,key:'Recruteur'}]}.
//  2. REPLI sur la convention de code `sos.recruiter.*` tant que tous les menus
//     recruteur ne sont pas tagués (rien ne casse pendant la transition).
// NB : ce n'est que de l'AFFICHAGE ; l'accès réel est gouverné serveur
// (app_profile_menu + @Secured).
function spaceValue(node) {
  const opts = Array.isArray(node?.options) ? node.options : [];
  const space = opts.find(o => o && String(o.code || o.property || '').includes('SOS_SPACE'));
  if (!space) return null;
  const v = space.value;
  const vals = (Array.isArray(v) ? v : (v == null ? [] : [v])).map(x => (x && x.key) ? x.key : x);
  if (vals.includes('Recruteur')) return 'Recruteur';
  if (vals.includes('Candidat')) return 'Candidat';
  return null;
}

function isRecruiterEntry(node) {
  const sv = spaceValue(node);
  if (sv) return sv === 'Recruteur';
  return typeof node?.code === 'string' && node.code.startsWith('sos.recruiter.');
}

function anyRecruiterEntry(nodes) {
  return (nodes || []).some(n => isRecruiterEntry(n) || anyRecruiterEntry(n.nodes));
}

/**
 * Filtre l'arbre de menu selon le mode :
 *  - recruiterMode=false (candidat) : on masque les entrées recruteur (sous-arbre SOS_RECRUITER) ;
 *  - recruiterMode=true  (recruteur) : on n'affiche QUE ces entrées (+ leurs parents).
 * Un parent est conservé s'il matche lui-même OU s'il lui reste des enfants visibles.
 */
function filterByMode(nodes, recruiterMode) {
  const out = [];
  (nodes || []).forEach(n => {
    // Zone « Hors menu » du profil : écran autorisé (routes, store) mais absent du tiroir.
    if (n.hidden) return;
    const children = filterByMode(n.nodes || [], recruiterMode);
    const selfMatches = recruiterMode ? isRecruiterEntry(n) : !isRecruiterEntry(n);
    if (selfMatches || children.length > 0) {
      out.push({ ...n, nodes: children });
    }
  });
  return out;
}

function loadContext() {
  try { return JSON.parse(localStorage.getItem('appMenu.context')) || {}; }
  catch (_) { return {}; }
}

const ctx = loadContext();

export const useAppMenuStore = defineStore(name, {

  id: name,

  state: () => ({
      menus: [],
      items: [],
      staticRoutes: [],
      dynamicRoutes: [],
      // Bascule candidat <-> recruteur (persisté) + société active sélectionnée.
      recruiterMode: !!ctx.recruiterMode,
      activeCompanyId: ctx.activeCompanyId || null,
      activeCompanyName: ctx.activeCompanyName || null,
    }
  ),

  actions: {
    /**
     * Load menu data from backend
     * @param {Array} payload - Menu items from backend
     */
    load(payload) {
      this.menus = payload.filter((v) => v.mode !=='global');
      this.items = this.extractMenuItems(payload);
    },

    /**
     * Store route references (actual routing is handled by routerService)
     * @param {Array} staticRoutes - Static routes from routes.js
     * @param {Array} dynamicRoutes - Generated dynamic routes
     */
    setRoutes(staticRoutes, dynamicRoutes) {
      this.staticRoutes = staticRoutes;
      this.dynamicRoutes = dynamicRoutes;
    },

    /** Active/désactive le mode recruteur (filtrage du menu) — persisté. */
    setRecruiterMode(flag) {
      this.recruiterMode = !!flag;
      this._persistContext();
    },

    /** Société active sélectionnée (défaut des filtres recruteur) — persisté. */
    setActiveCompany(id, name = null) {
      this.activeCompanyId = id || null;
      this.activeCompanyName = name || null;
      this._persistContext();
    },

    _persistContext() {
      try {
        localStorage.setItem('appMenu.context', JSON.stringify({
          recruiterMode: this.recruiterMode,
          activeCompanyId: this.activeCompanyId,
          activeCompanyName: this.activeCompanyName,
        }));
      } catch (_) { /* ignore */ }
    },

    /**
     * Clear menu and routes data
     */
    clear() {
      this.menus = [];
      this.items = [];
      this.staticRoutes = [];
      this.dynamicRoutes = [];
      this.recruiterMode = false;
      this.activeCompanyId = null;
      this.activeCompanyName = null;
      this._persistContext();
    },

    /**
     * Extract flat list of menu items for quick lookup
     * @param {Array} data - Hierarchical menu data
     * @returns {Array} Flat array of menu items
     */
    extractMenuItems(data) {
      const items = [];

      function processNode(node) {
        items.push({
          id : node.id,
          prevId : node.prevId,
          menuKey: node.menuKey,
          icon: node.icon,
          code: node.code,
          description: node.description,
          goTo : node.goTo,
          mode  : node.mode,
          nodes : node.nodes,
          options: node.options || [],
          // From route
          name : node.route?.name,
        });

        if (node.nodes && node.nodes.length > 0) {
          node.nodes.forEach(childNode => processNode(childNode));
        }
      }

      data.forEach(item => processNode(item));
      return items;
    }
  },

  getters: {
    /**
     * Menu filtré selon le mode candidat/recruteur (consommé par le sidebar).
     * Garde-fou : si recruiterMode est vrai (persisté) mais que l'utilisateur n'a
     * AUCUNE entrée recruteur (autre login non-recruteur), on retombe en mode
     * candidat pour ne pas afficher un menu vide.
     */
    visibleMenus: (state) => {
      const mode = state.recruiterMode && anyRecruiterEntry(state.menus);
      return filterByMode(state.menus, mode);
    },

    /**
     * Visibilité du switch candidat<->recruteur : RÔLE du profil — ROLE_RECRUITER
     * (recruteur) OU ROLE_ADMIN (l'admin peut aussi gérer une société / basculer) —
     * ET présence d'au moins une entrée recruteur dans le menu chargé. Sans cette
     * seconde condition, un admin d'une autre marque (RMS, Talent…) voyait « Créer
     * ma société » et appelait /sosRecruiterCompany/myCompanies (403). Le widget
     * appRecruiterModeSwitch gère ensuite le cas « pas de société ». L'accès réel
     * est gouverné serveur (app_profile_menu + @Secured).
     */
    hasRecruiterMenus: (state) => {
      try {
        const auth = useAuthStore();
        return (auth.hasPermission('ROLE_RECRUITER') || auth.hasPermission('ROLE_ADMIN'))
          && anyRecruiterEntry(state.menus);
      } catch (_) { return false; }
    },

    /**
     * Find menu item by menuKey
     */
    retrieveItem: (state) => (menuKey) => {
      return state.items.find((v) => v.menuKey === menuKey);
    },
    retrieveItemById: (state) => (id) => {
      return state.items.find((v) => v.id === id);
    },

    getChildrenByItemId: (state) => (itemId) => {
      return state.items.filter((v) => v.prevId === parseInt(itemId));
    },
    getChildrenByMenuKey: (state) => (menuKey) => {
      const item = state.items.find((v) => v.menuKey === menuKey);
      return state.items.filter((v) => v.prevId === item.id);
    },

    /**
     * Check if routes are loaded
     */
    hasRoutes: (state) => {
      return state.staticRoutes.length > 0 || state.dynamicRoutes.length > 0;
    }
  }
})
